<template>
  <v-container>
    <v-toolbar
      flat
      dense
      :color="($vuetify.theme.dark) ? '' : 'grey lighten-5'"
    >
      <span class="subtitle-2">Identifier</span>&nbsp; {{ identifier }}
      <v-spacer/>
      <span v-if="loading" style="width: 200px" class="mx-4">
        <v-progress-linear
          color="secondary"
          indeterminate
          buffer-value="0"
        ></v-progress-linear>
      </span>
      <span v-else-if="shadowTimestamp" class="caption secondary--text mr-4  d-none d-lg-inline-block"><strong>Version:</strong> {{ shadowVersion }}, <strong>Last Updated:</strong> {{ shadowTimestamp | moment("YYYY/MM/DD hh:mm:ss a") }}</span>
      <v-btn
        x-small
        outlined
        class="mr-1"
        color="secondary"
        @click="reloadShadow"
        tooltip="Reload Configuration"
        v-if="device.canEdit()"
        :disabled="loading"
      >
        Reload
      </v-btn>
      <v-btn
        x-small
        outlined
        color="accent"
        @click="refreshShadow"
        tooltip="Request config directly from the device"
        v-if="device.canEdit()"
        :disabled="loading"
      >
        Force Sync
      </v-btn>
    </v-toolbar>
    <v-row>
      <v-col cols="12" class="">
        <div v-if="isBasicConfig">
          <v-tabs
            v-model="configTab"
            :background-color="($vuetify.theme.dark) ? '' : 'grey lighten-5'"
            color="secondary"
          >
            <v-tabs-slider></v-tabs-slider>
            <v-tab href="#basic">
              Configuration
            </v-tab>
            <v-tab href="#json">
              JSON
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="configTab" class="px-3 pt-3">
            <v-tab-item value="basic">
              <v-row
                v-for="(value, key) of JSONConfig"
                v-bind:key="key"
                no-gutters
              >
                <v-checkbox
                  v-if="typeof value == 'boolean'"
                  v-model="JSONConfig[key]"
                  :label="$helpers.getLabel(key)"
                  @change="JSONConfigChanged"
                  :disabled="loading || !canEdit"
                  class="my-2"
                  hide-details
                ></v-checkbox>
                
                <v-text-field
                  v-else-if="typeof value == 'number'"
                  v-model.number="JSONConfig[key]"
                  type="number"
                  :label="$helpers.getLabel(key)"
                  @change="JSONConfigChanged"
                  :disabled="loading || !canEdit"
                  class="my-2"
                  outlined
                  hide-details
                ></v-text-field>
                
                <v-text-field
                  v-else
                  v-model="JSONConfig[key]"
                  :label="$helpers.getLabel(key)"
                  @change="JSONConfigChanged"
                  :disabled="loading || !canEdit"
                  class="my-2"
                  outlined
                  hide-details
                ></v-text-field>
              </v-row>
            </v-tab-item>
            <v-tab-item value="json">
              <v-textarea
                class="mt-3"
                label="JSON"
                v-model="JSONConfigString"
                @input="JSONConfigStringChanged"
                :disabled="loading || !canEdit"
                :messages="validationMessages"
                rows="10"
                outlined
                hide-details="auto"
              >
              </v-textarea>
            </v-tab-item>
          </v-tabs-items>
        </div>
        <v-textarea
          v-else
          class="mt-3"
          label="Configuration JSON"
          v-model="JSONConfigString"
          @input="JSONConfigStringChanged"
          :disabled="loading || !canEdit"
          :messages="validationMessages"
          rows="10"
          outlined
          hide-details="auto"
        >
        </v-textarea>
      </v-col>
    </v-row>
    
    <div class="mt-5 text-right" v-if="canEdit">
      <v-btn
        color="primary"
        class="ma-1"
        @click="saveShadow()"
        :disabled="loading || hasValidationError || !canEdit"
      >
        Save
      </v-btn>
    </div>
  </v-container>
</template>

<script>
  export default {
    name: 'ConfigureContainer',
    
    props: [
      'device_guid', 
      'container', 
      'canEdit',
      'isOnline',
      'isProcessing',
      'actionedContainer',
      'showDeleteContainer',
    ],
    
    data() {
      return {
        configTab: 'basic',
        
        documents: [],
        hasChanges: false,
        
        JSONConfig: {},
        JSONConfigString: '',
        
        hasValidationError: false,
        validationMessages: [],
      }
    },
    
    computed: {
      device() {
        return this.$devices.getDevice(this.device_guid)
      },
      
      deviceShadow() {
        if (this.device_guid) {
          return this.$deviceShadows.getDeviceShadow(this.device_guid, 'Containers')
        }
        
        return false
      },
      
      shadowTimestamp() {
        if (this.deviceShadow && this.deviceShadow.reported.timestamp) {
          return this.deviceShadow.reported.timestamp
        }
        
        return false
      },
      
      shadowVersion() {
        return this.deviceShadow.version()
      },
      
      loading() {
        if (this.deviceShadow && (!this.deviceShadow.ready || this.deviceShadow.updatePending)) {
          return true
        }
        
        return false
      },
      
      updatePending() {
        if (this.deviceShadow && this.deviceShadow.updatePending) {
          return true
        }
        
        return false
      },
      
      error() {
        if (this.deviceShadow && this.deviceShadow.error) {
          return true
        }
        
        return false
      }, 
      
      errorText() {
        if (this.deviceShadow && this.deviceShadow.errorText) {
          return this.deviceShadow.errorText
        }
        
        return false
      },
      
      identifier() {
        if (this.container.identifier) {
          return this.container.identifier
        }
        return false
      },
      
      isBasicConfig() {
        if (this.JSONConfig) {
          // if the item is an object or array, return false
          for (var item in this.JSONConfig) {
            if (typeof this.JSONConfig[item] === 'object' || Array.isArray(this.JSONConfig[item])) {
              return false
            }
          }
          return true
        }
        return false
      }
    },
    
    mounted() {
      console.log('ConfigureContainer ' + this.device_guid + ' mounted ' + this.container.id)
      
      this.deviceShadow.editing = true
      
      var documents = this.deviceShadow.editableStateCopy()
      if (documents) {
        this.documents = documents
        this.hasChanges = false
        this.setJSONConfig()
      }
    },
    
    beforeDestroy() {
      this.deviceShadow.editing = false
    },
    
    watch: {
      deviceShadow: {
        handler() {
          if (!this.hasChanges) {
            console.log('ConfigureContainer ' + this.device_guid + ' deviceShadow change ' + this.container.id)
            
            var documents = this.deviceShadow.editableStateCopy()
            if (documents) {
              this.documents = documents
              this.setJSONConfig()
            }
          }
        }, 
        deep: true
      }
    },
    
    methods: {
      setJSONConfig() {
        var shadowItem = this.documents.find((config) => config.id == this.identifier)
        
        if (!shadowItem) {
          console.log('ConfigureContainer ' + this.device_guid + ' setJSONConfig creating placeholder shadow config ' + this.container.id)
          shadowItem = {'id': this.identifier, 'config': {}}
        }
        
        this.JSONConfig = shadowItem.config
        this.JSONConfigChanged()
      },
      
      JSONConfigChanged() {
        this.JSONConfigString = JSON.stringify(this.JSONConfig, undefined, 4)
        this.hasValidationError = false
        this.validationMessages = []
      },
      
      JSONConfigStringChanged() {
        try {
          this.JSONConfig = JSON.parse(this.JSONConfigString)
          this.hasValidationError = false
          this.validationMessages = []
        } catch (error) {
          this.hasValidationError = true
          this.validationMessages = ['Invalid JSON']
        }
        this.hasChanges = true
      },
      
      saveShadow() {
        console.log('ConfigureContainer ' + this.device_guid + ' saveShadow ' + this.container.id, this.shadowItem)
        
        var itemIndex = this.documents.findIndex((config) => config.id == this.identifier)
        if (itemIndex >= 0) {
          this.documents[itemIndex].config = this.JSONConfig
        } else {
          console.log('ConfigureContainer ' + this.device_guid + ' saveShadow creating placeholder shadow config ' + this.container.id)
          
          this.documents.push({'id': this.identifier, 'config': this.JSONConfig})
        }
        
        var state = this.documents.map((config) => {
          return {'id': config.id, 'config': config.config}
        })
        
        this.deviceShadow.saveShadow(state)
        this.hasChanges = false
      },
      
      reloadShadow() {
        if (this.deviceShadow) {
          this.hasChanges = false
          this.deviceShadow.resetError()
          this.deviceShadow.fetchShadow(true)
        }
      },
      
      refreshShadow() {
        if (this.deviceShadow) {
          this.hasChanges = false
          this.deviceShadow.resetError()
          this.deviceShadow.requestShadowUpdate()
        }
      }
    }
  }
</script>